/**
 * FeatureAnnouncementPage — admin tool to compose and broadcast Wide-Hybrid
 * feature announcement emails to all active DrawReady users.
 *
 * Surfaced at /admin/announcements (route added in App.js).
 *
 * Workflow:
 *   1. Compose subject / headline / description / tagline / benefits list / CTA.
 *   2. Click "Send to me first" → fires a single test send (no version bump).
 *   3. Click "Send to all active users" → confirmation modal shows eligible
 *      count, on confirm dispatches the broadcast and records in history.
 *   4. Past broadcasts shown below.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Megaphone, Send, Plus, X, Loader2, AlertTriangle, History, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const newBenefit = () => '';

export default function FeatureAnnouncementPage() {
  const navigate = useNavigate();
  const [audience, setAudience] = useState({ total_users: 0, eligible: 0, filtered_out: 0 });
  const [history, setHistory] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [form, setForm] = useState({
    subject: '',
    feature_name: '',
    description: '',
    tagline: '',
    benefits: [''],
    cta_text: 'Open my dashboard',
    cta_url: 'https://drawready.com/dashboard',
    version_tag: '',
  });

  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const reloadMeta = async () => {
    setLoadingMeta(true);
    try {
      const [aud, hist] = await Promise.all([
        api.get('/admin/feature-announcement/audience'),
        api.get('/admin/feature-announcement/history'),
      ]);
      setAudience(aud.data);
      setHistory(hist.data || []);
    } catch (e) {
      toast.error('Could not load audience info');
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => { reloadMeta(); }, []);

  const updateBenefit = (idx, value) => {
    const next = [...form.benefits];
    next[idx] = value;
    setForm({ ...form, benefits: next });
  };
  const addBenefit = () => setForm({ ...form, benefits: [...form.benefits, newBenefit()] });
  const removeBenefit = (idx) => {
    const next = form.benefits.filter((_, i) => i !== idx);
    setForm({ ...form, benefits: next.length ? next : [''] });
  };

  const validate = () => {
    if (!form.subject?.trim()) return 'Subject required';
    if (!form.feature_name?.trim()) return 'Feature name required';
    if (!form.description?.trim()) return 'Description required';
    return null;
  };

  const sendTest = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSending(true);
    try {
      const body = {
        ...form,
        benefits: form.benefits.filter(b => b.trim()),
        test_email: 'modteetsw@gmail.com',
      };
      const res = await api.post('/admin/feature-announcement/send', body);
      toast.success(res.data.ok ? 'Test email sent to modteetsw@gmail.com' : 'Test send failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  const sendBroadcast = async () => {
    const err = validate();
    if (err) { setConfirmOpen(false); return toast.error(err); }
    setSending(true);
    try {
      const body = {
        ...form,
        benefits: form.benefits.filter(b => b.trim()),
        test_email: null,
      };
      // The backend requires X-Admin-Email for broadcast; pull from localStorage.user
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const adminEmail = stored?.email || '';
      const res = await api.post('/admin/feature-announcement/send', body, {
        headers: { 'X-Admin-Email': adminEmail },
      });
      const { sent, failed, skipped } = res.data;
      toast.success(`Broadcast complete · ${sent} sent · ${failed} failed · ${skipped} filtered`);
      setConfirmOpen(false);
      reloadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-6" data-testid="feature-announcement-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/admin')}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2"
          data-testid="announcements-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </button>

        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-bold text-slate-900">Feature Announcements</h1>
          </div>
          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
            {loadingMeta ? '…' : `${audience.eligible} eligible users`}
          </Badge>
        </div>

        <Card className="p-6 bg-white border-slate-200 space-y-4" data-testid="compose-card">
          <div>
            <Label className="text-xs font-semibold">Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="New: Contract Summary PDFs auto-attached to awards"
              data-testid="announce-subject"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Headline (Feature name)</Label>
              <Input
                value={form.feature_name}
                onChange={(e) => setForm({ ...form, feature_name: e.target.value })}
                placeholder="Contract Summary PDFs"
                data-testid="announce-headline"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Version tag (optional)</Label>
              <Input
                value={form.version_tag}
                onChange={(e) => setForm({ ...form, version_tag: e.target.value })}
                placeholder="contract_summary_pdf_v1"
                data-testid="announce-version"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Description (lead paragraph)</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Every award now generates a branded contract summary PDF, auto-attached to the award email and stored in the contractor portal."
              data-testid="announce-description"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">Tagline (optional)</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="No more chasing for signed contracts before draw funding."
              data-testid="announce-tagline"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">Benefits / what's new</Label>
            <div className="space-y-2">
              {form.benefits.map((b, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={b}
                    onChange={(e) => updateBenefit(idx, e.target.value)}
                    placeholder="Auto-generated when you click Award Bid"
                    data-testid={`announce-benefit-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeBenefit(idx)}
                    className="text-slate-400 hover:text-red-500 p-1"
                    data-testid={`announce-benefit-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addBenefit} data-testid="announce-benefit-add">
                <Plus className="w-3 h-3 mr-1" /> Add benefit
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">CTA button text</Label>
              <Input
                value={form.cta_text}
                onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                data-testid="announce-cta-text"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">CTA URL</Label>
              <Input
                value={form.cta_url}
                onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                data-testid="announce-cta-url"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={sending}
              data-testid="announce-send-test"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send to me first
            </Button>
            <Button
              onClick={() => { if (!validate()) setConfirmOpen(true); else toast.error(validate()); }}
              disabled={sending || audience.eligible === 0}
              data-testid="announce-broadcast-btn"
            >
              <Megaphone className="w-3.5 h-3.5 mr-1" />
              Send to all {audience.eligible} active users
            </Button>
          </div>
        </Card>

        {/* History */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-bold text-slate-700">Recent broadcasts</h2>
          </div>
          {history.length === 0 ? (
            <Card className="p-6 text-center text-xs text-slate-500" data-testid="announce-history-empty">
              No broadcasts sent yet.
            </Card>
          ) : (
            <Card className="overflow-hidden divide-y divide-slate-100">
              {history.map((h, i) => (
                <div key={h.id || i} className="px-4 py-3 flex items-center gap-3" data-testid={`announce-history-row-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{h.feature_name || h.subject}</p>
                    <p className="text-[11px] text-slate-500">
                      {h.sent_at?.slice(0, 16).replace('T', ' ')} · {h.sent_count} sent · {h.failed_count} failed · {h.skipped_count || 0} skipped
                    </p>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Confirmation modal */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="sm:max-w-md" data-testid="announce-confirm-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Megaphone className="w-5 h-5" /> Broadcast to {audience.eligible} users
              </DialogTitle>
              <DialogDescription>
                This will send the announcement email to every active DrawReady user. Subject: <strong>{form.subject}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>{audience.filtered_out}</strong> test/demo accounts will be automatically skipped.
                If anything looks off, hit "Send to me first" instead.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
              <Button onClick={sendBroadcast} disabled={sending} data-testid="announce-confirm-broadcast">
                {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</> : <>Send broadcast</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
